import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Button,
  Space,
  Table,
  Tag,
  message,
  Row,
  Col,
  Divider,
  Tooltip
} from 'antd';
import {
  DeleteOutlined,
  PlusOutlined,
  ImportOutlined,
  SaveOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import creditNoteService, {
  CreditNote,
  CreditNoteItem,
  CreateCreditNoteInput
} from '../../../../services/creditNoteService';
import invoiceService, { Invoice } from '../../../../services/invoiceService';
import apiService from '../../../../services/apiService';
import { useDraggableRows } from '../../../../components/table/useDraggableRows';
import { useTranslation } from "react-i18next";
import { findSalesJournal } from '../../utils/salesJournal';

const { Option } = Select;
const { TextArea } = Input;

interface CreditNoteFormProps {
  mode: 'create' | 'edit' | 'view' | 'duplicate';
  initialValue: CreditNote | null;
  onClose: () => void;
  onSaved: () => void;
}

type ItemRow = Required<Pick<CreditNoteItem, 'description' | 'quantity' | 'unitPrice' | 'lineTotal'>> &
  Partial<Omit<CreditNoteItem, 'description' | 'quantity' | 'unitPrice' | 'lineTotal'>> & {
    _key: string;
  };

const round2 = (n: number) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

const buildEmptyItem = (sortOrder = 0): ItemRow => ({
  _key: `r${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
  description: '',
  quantity: 1,
  unit: 'unit(s)',
  unitPrice: 0,
  discount: 0,
  discountType: 'fixed',
  taxRate: 0,
  taxAmount: 0,
  lineTotal: 0,
  accountId: null,
  departmentId: null,
  departmentName: null,
  subcategory: null,
  itemCode: null,
  sortOrder
});

const computeLineTotal = (row: ItemRow): number => {
  const qty = Number(row.quantity || 0);
  const price = Number(row.unitPrice || 0);
  const disc = Number(row.discount || 0);
  let subtotal = qty * price;
  if (row.discountType === 'percentage') {
    subtotal = subtotal * (1 - disc / 100);
  } else {
    subtotal = subtotal - disc;
  }
  if (row.taxRate && row.taxRate > 0) {
    subtotal = subtotal * (1 + Number(row.taxRate) / 100);
  }
  return round2(subtotal);
};

const CreditNoteForm: React.FC<CreditNoteFormProps> = ({ mode, initialValue, onClose, onSaved }) => {
    const { t } = useTranslation();
  const isView = mode === 'view';
  const isEdit = mode === 'edit';
  const isCreate = mode === 'create' || mode === 'duplicate';

  const [form] = Form.useForm();
  const [items, setItems] = useState<ItemRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState<Array<{ id: string; name: string }>>([]);
  const [customerInvoices, setCustomerInvoices] = useState<Invoice[]>([]);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [departments, setDepartments] = useState<any[]>([]);

  // Departments are still fetched, but ONLY to render a line saved before this
  // field was unlinked from HR. They are never offered as choices.
  const legacyDepartmentName = (departmentId?: string | null): string => {
    if (!departmentId) return '';
    const d = departments.find((x: any) => x.id === departmentId);
    return d ? `${d.code} - ${d.name}` : '';
  };
  const [journals, setJournals] = useState<any[]>([]);
  const [accountingSettings, setAccountingSettings] = useState<any>(null);
  const [currency, setCurrency] = useState<string>('HKD');

  const drag = useDraggableRows<ItemRow>(items, setItems);

  // ---- Bootstrap reference data ----
  useEffect(() => {
    (async () => {
      try {
        const [custResp, acctResp, jrnResp, settingsResp, deptResp] = await Promise.all([
          apiService.get<any>('/customers?limit=1000'),
          apiService.get<any>('/accounting/accounts'),
          apiService.get<any>('/accounting/journals/active'),
          apiService.get<any>('/accounting/settings'),
          // Departments come from HR; the invoice line items use the same list.
          apiService.get<any>('/hr/departments?limit=500').catch(() => null)
        ]);
        const custList = custResp?.data?.customers || custResp?.customers || custResp?.data || [];
        setCustomers(custList.map((c: any) => ({ id: c.id, name: c.name })));
        setAccounts(acctResp?.data || []);
        setJournals(jrnResp?.data || []);
        setAccountingSettings(settingsResp?.data || null);
        const deptList = deptResp?.data?.departments || deptResp?.data || [];
        setDepartments(Array.isArray(deptList) ? deptList : []);
      } catch (err) {
        console.error('Failed to load reference data', err);
      }
    })();
  }, []);

  // ---- Seed form from initialValue ----
  useEffect(() => {
    if (initialValue) {
      const formValues = {
        creditNoteNo: mode === 'duplicate' ? undefined : initialValue.creditNoteNo,
        customerId: initialValue.customerId,
        invoiceId: initialValue.invoiceId || undefined,
        creditNoteDate: mode === 'duplicate' ? dayjs() : (initialValue.creditNoteDate ? dayjs(initialValue.creditNoteDate) : dayjs()),
        currency: initialValue.currency || 'HKD',
        exchangeRate: initialValue.exchangeRate || undefined,
        reason: initialValue.reason || '',
        notes: initialValue.notes || '',
        termsConditions: initialValue.termsConditions || ''
      };
      form.setFieldsValue(formValues);
      setCurrency(initialValue.currency || 'HKD');

      const seededItems = (initialValue.items || []).map((it, idx) => ({
        _key: `r${idx}_${Math.random().toString(36).slice(2, 7)}`,
        description: it.description,
        itemCode: it.itemCode,
        subcategory: it.subcategory,
        quantity: Number(it.quantity || 0),
        unit: it.unit || 'unit(s)',
        unitPrice: Number(it.unitPrice || 0),
        discount: Number(it.discount || 0),
        discountType: (it.discountType as any) || 'fixed',
        taxRate: Number(it.taxRate || 0),
        taxAmount: Number(it.taxAmount || 0),
        lineTotal: Number(it.lineTotal || 0),
        accountId: it.accountId,
        departmentId: (it as any).departmentId ?? null,
        departmentName: (it as any).departmentName ?? null,
        sortOrder: it.sortOrder ?? idx
      })) as ItemRow[];
      setItems(seededItems.length > 0 ? seededItems : [buildEmptyItem(0)]);

      // Load invoices for this customer to populate the Linked Invoice dropdown
      if (initialValue.customerId) {
        loadCustomerInvoices(initialValue.customerId);
      }
    } else {
      form.setFieldsValue({ creditNoteDate: dayjs(), currency: 'HKD' });
      setItems([buildEmptyItem(0)]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue, mode]);

  const loadCustomerInvoices = async (customerId: string) => {
    try {
      const list = await invoiceService.getInvoicesByCustomer(customerId);
      // Only invoices that can be credited: SENT, OVERDUE, PAID
      setCustomerInvoices((list || []).filter(inv => ['SENT', 'OVERDUE', 'PAID'].includes(inv.status)));
    } catch (err) {
      console.error('Failed to load customer invoices', err);
      setCustomerInvoices([]);
    }
  };

  const handleCustomerChange = (customerId: string) => {
    form.setFieldsValue({ invoiceId: undefined });
    loadCustomerInvoices(customerId);
  };

  const onCopyFromInvoice = async () => {
    const invoiceId = form.getFieldValue('invoiceId');
    if (!invoiceId) {
      message.info(t('Pick a linked invoice first.'));
      return;
    }
    const proceed = async () => {
      try {
        const inv = await invoiceService.getInvoiceById(invoiceId);
        const newItems: ItemRow[] = (inv.items || []).map((it, idx) => ({
          _key: `r${idx}_${Math.random().toString(36).slice(2, 7)}`,
          description: it.description,
          itemCode: it.itemCode,
          subcategory: (it as any).subcategory ?? null,
          quantity: Number(it.quantity || 0),
          unit: it.unit || 'unit(s)',
          unitPrice: Number(it.unitPrice || 0),
          discount: Number(it.discount || 0),
          discountType: (it.discountType as any) || 'fixed',
          taxRate: Number(it.taxRate || 0),
          taxAmount: Number(it.taxAmount || 0),
          lineTotal: Number(it.lineTotal || 0),
          accountId: it.accountId,
          departmentId: (it as any).departmentId ?? null,
          departmentName: (it as any).departmentName ?? null,
          sortOrder: idx
        }));
        setItems(newItems);
        form.setFieldsValue({
          currency: inv.currency,
          exchangeRate: (inv as any).exchangeRate
        });
        setCurrency(inv.currency);
        message.success(t('Copied {{length}} line(s) from {{invoiceNo}}', { length: newItems.length, invoiceNo: inv.invoiceNo }));
      } catch (err: any) {
        message.error(err?.message || t('Failed to copy from invoice'));
      }
    };
    if (items.some(r => r.description || r.lineTotal)) {
      Modal.confirm({
        title: t('Replace current line items?'),
        content: t('The items below will be replaced with the lines from the linked invoice.'),
        onOk: proceed
      });
    } else {
      proceed();
    }
  };

  const updateItem = (key: string, patch: Partial<ItemRow>) => {
    setItems(rows =>
      rows.map(r => {
        if (r._key !== key) return r;
        const merged = { ...r, ...patch };
        merged.lineTotal = computeLineTotal(merged);
        return merged;
      })
    );
  };

  const removeItem = (key: string) => {
    setItems(rows => (rows.length <= 1 ? rows : rows.filter(r => r._key !== key)));
  };

  const addItem = () => {
    setItems(rows => [...rows, buildEmptyItem(rows.length)]);
  };

  const totalAmount = useMemo(
    () => round2(items.reduce((s, r) => s + Number(r.lineTotal || 0), 0)),
    [items]
  );

  // ---- Submit ----
  const resolveAccountIds = (): { arAccountId: string; revenueAccountId: string; journalId: string } | null => {
    const arById = accountingSettings?.defaultReceivableAccountId
      ? accounts.find(a => a.id === accountingSettings.defaultReceivableAccountId)
      : null;
    const arAccount = arById
      || accounts.find(a => a.code === '124000')
      || accounts.find(a => a.code?.startsWith('1200'));

    const revenueById = accountingSettings?.defaultRevenueAccountId
      ? accounts.find(a => a.id === accountingSettings.defaultRevenueAccountId)
      : null;
    const revenueAccount = revenueById
      || accounts.find(a => a.code?.includes('4000') || a.code?.startsWith('41'));

    const journal = findSalesJournal(journals);

    if (!arAccount || !revenueAccount || !journal) {
      message.error(t('Missing AR account, revenue account, or INV/SAL journal. Configure them in Accounting Settings.'));
      return null;
    }
    return {
      arAccountId: arAccount.id,
      revenueAccountId: revenueAccount.id,
      journalId: journal.id
    };
  };

  const buildPayload = useCallback((values: any): CreateCreditNoteInput => ({
    customerId: values.customerId,
    invoiceId: values.invoiceId || null,
    creditNoteDate: values.creditNoteDate ? dayjs(values.creditNoteDate).format('YYYY-MM-DD') : undefined,
    totalAmount,
    currency: values.currency || 'HKD',
    exchangeRate: values.exchangeRate || undefined,
    reason: values.reason || null,
    notes: values.notes || null,
    termsConditions: values.termsConditions || null,
    items: items.map((it, idx) => ({
      description: it.description,
      itemCode: it.itemCode,
      subcategory: it.subcategory,
      quantity: Number(it.quantity || 0),
      unit: it.unit,
      unitPrice: Number(it.unitPrice || 0),
      discount: Number(it.discount || 0),
      discountType: it.discountType,
      taxRate: Number(it.taxRate || 0),
      taxAmount: Number(it.taxAmount || 0),
      lineTotal: Number(it.lineTotal || 0),
      accountId: it.accountId,
      departmentId: it.departmentId ?? null,
      departmentName: (it as any).departmentName ?? null,
      sortOrder: idx
    }))
  }), [items, totalAmount]);

  const handleSave = async (postAfter: boolean) => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    if (items.length === 0 || items.every(it => !it.description || !it.lineTotal)) {
      message.error(t('At least one line item with description and amount is required.'));
      return;
    }

    setLoading(true);
    try {
      const payload = buildPayload(values);
      let saved: CreditNote;

      if (isEdit && initialValue?.id) {
        saved = await creditNoteService.updateCreditNote(initialValue.id, payload);
        message.success(t('Credit note updated'));
      } else {
        saved = await creditNoteService.createCreditNote(payload);
        message.success(t('Credit note {{creditNoteNo}} created', { creditNoteNo: saved.creditNoteNo }));
      }

      if (postAfter && saved.status === 'DRAFT') {
        const accountIds = resolveAccountIds();
        if (!accountIds) {
          setLoading(false);
          return;
        }
        try {
          await creditNoteService.postCreditNote(saved.id, {
            accountsReceivableAccountId: accountIds.arAccountId,
            revenueAccountId: accountIds.revenueAccountId,
            journalId: accountIds.journalId
          });
          message.success(t('Credit note posted to GL'));
        } catch (err: any) {
          message.error(t('Saved but failed to post: {{err}}', { err: err?.message || err }));
          setLoading(false);
          return;
        }
      }

      onSaved();
    } catch (err: any) {
      message.error(t('Failed to save: {{err}}', { err: err?.message || err }));
    } finally {
      setLoading(false);
    }
  };

  const handleCancelPosted = async () => {
    if (!initialValue?.id) return;
    Modal.confirm({
      title: t('Cancel {{creditNoteNo}}?', { creditNoteNo: initialValue.creditNoteNo }),
      content: t('A reversing journal entry will be posted. This action cannot be undone.'),
      okType: 'danger',
      onOk: async () => {
        const accountIds = resolveAccountIds();
        if (!accountIds) return;
        try {
          await creditNoteService.cancelCreditNote(initialValue.id, {
            accountsReceivableAccountId: accountIds.arAccountId,
            revenueAccountId: accountIds.revenueAccountId,
            journalId: accountIds.journalId
          });
          message.success(t('Credit note cancelled'));
          onSaved();
        } catch (err: any) {
          message.error(err?.message || t('Failed to cancel'));
        }
      }
    });
  };

  // ---- Items table columns ----
  const itemColumns = [
    drag.dragHandleColumn,
    {
      title: '#',
      key: 'idx',
      width: 40,
      render: (_: any, __: any, idx: number) => idx + 1
    },
    {
      title: t('Item Title / Description'),
      dataIndex: 'description',
      key: 'description',
      render: (_: any, record: ItemRow) => (
        <Input
          value={record.description}
          disabled={isView}
          onChange={e => updateItem(record._key, { description: e.target.value })}
        />
      )
    },
    {
      title: t('Subcategory'),
      dataIndex: 'subcategory',
      key: 'subcategory',
      width: 140,
      render: (_: any, record: ItemRow) => (
        <Input
          value={record.subcategory || ''}
          disabled={isView}
          placeholder="(optional)"
          onChange={e => updateItem(record._key, { subcategory: e.target.value || null })}
        />
      )
    },
    {
      title: t('Account'),
      dataIndex: 'accountId',
      key: 'accountId',
      width: 200,
      render: (_: any, record: ItemRow) => (
        <Select
          value={record.accountId || undefined}
          disabled={isView}
          allowClear
          showSearch
          optionFilterProp="children"
          placeholder={t('Default')}
          style={{ width: '100%' }}
          onChange={v => updateItem(record._key, { accountId: v || null })}
        >
          {accounts.map(a => (
            <Option key={a.id} value={a.id}>{a.code} - {a.name}</Option>
          ))}
        </Select>
      )
    },
    {
      // Same analytic dimension the invoice line items carry; it flows to the
      // journal entry line on post and shows in the General Ledger.
      title: t('Department'),
      dataIndex: 'departmentName',
      key: 'departmentName',
      width: 190,
      // Free text, not a picker — unlinked from the HR department list at the
      // client's request. Lines saved before the change show their old name,
      // resolved from the link, and keep it unless edited.
      render: (_: any, record: ItemRow) => (
        <Input
          value={(record as any).departmentName ?? legacyDepartmentName(record.departmentId)}
          disabled={isView}
          placeholder={t('Department')}
          onChange={e => updateItem(record._key, { departmentName: e.target.value } as any)}
        />
      )
    },
    {
      title: t('Qty'),
      dataIndex: 'quantity',
      key: 'quantity',
      width: 90,
      render: (_: any, record: ItemRow) => (
        <InputNumber
          value={record.quantity}
          disabled={isView}
          min={0}
          step={1}
          style={{ width: '100%' }}
          onChange={v => updateItem(record._key, { quantity: Number(v || 0) })}
        />
      )
    },
    {
      title: t('Unit'),
      dataIndex: 'unit',
      key: 'unit',
      width: 100,
      render: (_: any, record: ItemRow) => (
        <Input
          value={record.unit || ''}
          disabled={isView}
          onChange={e => updateItem(record._key, { unit: e.target.value })}
        />
      )
    },
    {
      title: t('Unit Price'),
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      width: 120,
      render: (_: any, record: ItemRow) => (
        <InputNumber
          value={record.unitPrice}
          disabled={isView}
          step={0.01}
          precision={2}
          style={{ width: '100%' }}
          onChange={v => updateItem(record._key, { unitPrice: Number(v || 0) })}
        />
      )
    },
    {
      title: t('Discount'),
      dataIndex: 'discount',
      key: 'discount',
      width: 130,
      render: (_: any, record: ItemRow) => (
        <Space.Compact>
          <InputNumber
            value={record.discount}
            disabled={isView}
            min={0}
            step={0.01}
            style={{ width: 80 }}
            onChange={v => updateItem(record._key, { discount: Number(v || 0) })}
          />
          <Select
            value={record.discountType}
            disabled={isView}
            style={{ width: 50 }}
            onChange={v => updateItem(record._key, { discountType: v })}
          >
            <Option value="fixed">$</Option>
            <Option value="percentage">%</Option>
          </Select>
        </Space.Compact>
      )
    },
    {
      title: t('Line Total'),
      dataIndex: 'lineTotal',
      key: 'lineTotal',
      width: 120,
      align: 'right' as const,
      render: (v: number) => (v ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    {
      title: '',
      key: 'remove',
      width: 40,
      render: (_: any, record: ItemRow) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          disabled={isView || items.length <= 1}
          onClick={() => removeItem(record._key)}
        />
      )
    }
  ];

  const title =
    mode === 'create' ? 'New Credit Note' :
    mode === 'edit' ? `Edit ${initialValue?.creditNoteNo}` :
    mode === 'duplicate' ? 'Duplicate Credit Note' :
    `View ${initialValue?.creditNoteNo}`;

  const isPosted = initialValue?.status === 'POSTED';

  return (
    <Modal
      title={title}
      open
      width={1200}
      onCancel={onClose}
      maskClosable={false}
      footer={
        <Space>
          <Button onClick={onClose}>{t('Close')}</Button>
          {!isView && (isCreate || (isEdit && !isPosted)) && (
            <Button icon={<SaveOutlined />} onClick={() => handleSave(false)} loading={loading}>
              {t('Save as Draft')}
                                  </Button>
          )}
          {!isView && isCreate && (
            <Button type="primary" icon={<CheckCircleOutlined />} onClick={() => handleSave(true)} loading={loading}>
              {t('Save & Post')}
                                  </Button>
          )}
          {!isView && isEdit && initialValue?.status === 'DRAFT' && (
            <Button type="primary" icon={<CheckCircleOutlined />} onClick={() => handleSave(true)} loading={loading}>
              {t('Save & Post')}
                                  </Button>
          )}
          {!isView && isEdit && initialValue?.status === 'POSTED' && (
            <Button type="primary" onClick={() => handleSave(false)} loading={loading}>
              {t('Save')}
                                  </Button>
          )}
          {isView && isPosted && (
            <Button danger onClick={handleCancelPosted}>{t('Cancel Credit Note')}</Button>
          )}
        </Space>
      }
    >
      {initialValue?.status && (
        <div style={{ marginBottom: 12 }}>
          {t('Status:')}{' '}
          <Tag color={initialValue.status === 'POSTED' ? 'green' : initialValue.status === 'CANCELLED' ? 'red' : 'default'}>
            {initialValue.status}
          </Tag>
        </div>
      )}
      <Form form={form} layout="vertical" disabled={isView}>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name="customerId" label={t('Customer')} rules={[{ required: true, message: t('Customer is required') }]}>
              <Select
                showSearch
                optionFilterProp="children"
                onChange={handleCustomerChange}
                placeholder={t('Select customer')}
              >
                {customers.map(c => (
                  <Option key={c.id} value={c.id}>{c.name}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="creditNoteNo" label={t('Credit Note #')} extra={t('Auto-generated if blank')}>
              <Input placeholder="(auto)" disabled={isEdit && isPosted} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="creditNoteDate" label={t('Credit Note Date')} rules={[{ required: true }]}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label={t('Linked Invoice (optional)')}>
              <Space.Compact style={{ width: '100%' }}>
                <Form.Item name="invoiceId" noStyle>
                  <Select allowClear showSearch optionFilterProp="children" placeholder="(none)" style={{ width: '100%' }}>
                    {customerInvoices.map(inv => (
                      <Option key={inv.id} value={inv.id}>
                        {inv.invoiceNo} — {inv.currency} {Number(inv.totalAmount).toFixed(2)} ({inv.status})
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Tooltip title={t('Copy line items from the linked invoice')}>
                  <Button icon={<ImportOutlined />} onClick={onCopyFromInvoice} disabled={isView}>
                    {t('Copy')}
                                                        </Button>
                </Tooltip>
              </Space.Compact>
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="currency" label={t('Currency')} initialValue="HKD">
              <Select onChange={v => setCurrency(v)}>
                <Option value="HKD">HKD</Option>
                <Option value="USD">USD</Option>
                <Option value="CNY">CNY</Option>
                <Option value="EUR">EUR</Option>
                <Option value="GBP">GBP</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={6}>
            {currency !== 'HKD' && (
              <Form.Item name="exchangeRate" label={t('Exchange Rate (1 {{currency}} = ? HKD)', { currency })}>
                <InputNumber min={0} step={0.0001} precision={4} style={{ width: '100%' }} placeholder="auto" />
              </Form.Item>
            )}
          </Col>
        </Row>
        <Form.Item name="reason" label={t('Reason')}>
          <Input placeholder={t('Why this credit is being issued (e.g., returned goods, billing correction)')} />
        </Form.Item>

        <Divider>{t('Line Items')}</Divider>
        <Table<ItemRow>
          rowKey="_key"
          dataSource={items}
          columns={itemColumns as any}
          pagination={false}
          size="small"
          scroll={{ x: 'max-content' }}
          onRow={drag.onRow}
        />
        {!isView && (
          <Button icon={<PlusOutlined />} onClick={addItem} style={{ marginTop: 8 }}>
            {t('Add Line Item')}
                                </Button>
        )}

        <Row style={{ marginTop: 16 }}>
          <Col span={12} />
          <Col span={12}>
            <div style={{ background: '#fafafa', padding: 16, textAlign: 'right' }}>
              <div style={{ fontSize: 14, color: '#666' }}>{t('Total Amount')}</div>
              <div style={{ fontSize: 28, fontWeight: 600 }}>
                {currency} {totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
          </Col>
        </Row>

        <Divider />
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="notes" label={t('Notes')}>
              <TextArea rows={3} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="termsConditions" label={t('Terms & Conditions')}>
              <TextArea rows={3} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default CreditNoteForm;
